import Topbar from "@/components/Topbar";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Briefcase, Handshake, Users, ArrowRight } from "@phosphor-icons/react";

/* v2.90 · Repurposed "Projects" tab — the collaboration workspace where
   Clients see their posted jobs + hired PEs, and PEs see their active
   engagements. Full jobs board + proposals + chat land in the next
   session (see PRD next-actions). This page is the placeholder shell
   with role-appropriate CTAs so the nav item goes somewhere useful. */

export default function ProjectsHubPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isPE = role === "pe";
  const isClient = role === "client";

  return (
    <div className="industrial min-h-screen bg-[#0A0C10]">
      <Topbar />
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="label-tag">// projects</div>
        <h1 className="font-display text-4xl md:text-5xl font-black mt-1">
          {isPE ? "Your engagements" : isClient ? "Your project engagements" : "Marketplace engagements"}
        </h1>
        <p className="text-gray-400 mt-2 max-w-2xl">
          {isPE && "Client-hired projects you're actively working on. Job board + proposal submissions land in the next release."}
          {isClient && "Posted jobs and the Professional Engineers you've hired. Full job board + proposals + in-app chat land in the next release."}
          {role === "admin" && "Collaboration workspace — separate from the admin Studio (the CAD/panel design tool)."}
        </p>

        {role === "admin" && (
          <div className="mt-6 border border-[#FFCC00]/30 bg-[#FFCC00]/5 px-4 py-3 text-sm font-mono text-[#FFCC00]"
               data-testid="engagements-admin-hint">
            <span className="label-tag !text-[#FFCC00]/80">// admin</span>{" "}
            Your CAD workspace lives at{" "}
            <Link to="/studio" className="underline hover:text-white" data-testid="engagements-studio-link">/studio</Link>. This page is the marketplace side.
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
          {isClient && (
            <>
              <Link to="/jobs/new" className="border border-white/10 hover:border-[#00E5FF]/60 p-6 bg-[#12151C] transition group"
                    data-testid="engagements-tile-post-job">
                <div className="w-10 h-10 bg-[#00E5FF] flex items-center justify-center"><Briefcase size={22} weight="bold" className="text-black" /></div>
                <div className="label-tag mt-3">// post</div>
                <h3 className="font-display text-2xl font-bold mt-1 group-hover:text-[#00E5FF] transition">Post a project</h3>
                <p className="text-sm text-gray-400 mt-2">Describe the scope, budget, and deadline. Vetted PEs submit proposals.</p>
                <div className="mt-4 text-xs font-mono text-[#00E5FF] inline-flex items-center gap-1">Open form <ArrowRight size={12} /></div>
              </Link>
              <Link to="/directory" className="border border-white/10 hover:border-[#00E5FF]/60 p-6 bg-[#12151C] transition group"
                    data-testid="engagements-tile-directory">
                <Users size={28} weight="duotone" className="text-[#00E5FF]" />
                <div className="label-tag mt-3">// find</div>
                <h3 className="font-display text-2xl font-bold mt-1 group-hover:text-[#00E5FF] transition">Browse engineers</h3>
                <p className="text-sm text-gray-400 mt-2">Filter the directory by country, region, years of practice, and specialization.</p>
                <div className="mt-4 text-xs font-mono text-[#00E5FF] inline-flex items-center gap-1">Open directory <ArrowRight size={12} /></div>
              </Link>
            </>
          )}
          {isPE && (
            <>
              <Link to="/pe/profile" className="border border-white/10 hover:border-[#00E5FF]/60 p-6 bg-[#12151C] transition group"
                    data-testid="engagements-tile-profile">
                <Handshake size={28} weight="duotone" className="text-[#00E5FF]" />
                <div className="label-tag mt-3">// prepare</div>
                <h3 className="font-display text-2xl font-bold mt-1 group-hover:text-[#00E5FF] transition">Your profile</h3>
                <p className="text-sm text-gray-400 mt-2">Complete and submit your profile — clients see it in the directory.</p>
                <div className="mt-4 text-xs font-mono text-[#00E5FF] inline-flex items-center gap-1">Open profile <ArrowRight size={12} /></div>
              </Link>
              <Link to="/jobs" className="border border-white/10 hover:border-[#00E5FF]/60 p-6 bg-[#12151C] transition group"
                    data-testid="engagements-tile-jobs">
                <Briefcase size={28} weight="duotone" className="text-[#00E5FF]" />
                <div className="label-tag mt-3">// respond</div>
                <h3 className="font-display text-2xl font-bold mt-1 group-hover:text-[#00E5FF] transition">Job board</h3>
                <p className="text-sm text-gray-400 mt-2">Browse open client postings and submit RFP-style proposals.</p>
                <div className="mt-4 text-xs font-mono text-[#00E5FF] inline-flex items-center gap-1">Open jobs <ArrowRight size={12} /></div>
              </Link>
            </>
          )}
        </div>

        <div className="mt-10 border border-white/10 bg-white/5 p-4 text-xs font-mono text-gray-400" data-testid="engagements-info-banner">
          Active engagements will appear here once the job board + proposal flow ship. Until then, use the directory to discover engineers or your profile page to prepare your listing.
        </div>
      </div>
    </div>
  );
}
